/**
 * Обрезка изображения по прямоугольнику, выбранному пользователем.
 *
 * Кропер (`ud-form/components/image-crop`) считает только координаты области —
 * саму вырезку рисуем здесь, на тех же примитивах, что и сжатие. Это не лишняя
 * работа, а единственный способ не разойтись с `compress-image.ts` в двух вещах,
 * которые ломаются необратимо:
 *
 * 1. **EXIF-поворот** применяется при декодировании (`decodeImage`), иначе
 *    координаты области с вертикального фото с iPhone указывали бы не туда.
 * 2. **Прозрачность** решает формат выхода: есть альфа — WebP, нет — JPEG.
 *    Обрезка всегда перекодирует файл, поэтому вырезанный по контуру логотип,
 *    отданный в JPEG, потерял бы фон навсегда.
 *
 * Порядок в дропзоне — сначала кроп, потом сжатие: обрезка уменьшает картинку, и
 * после неё файл часто проходит порог сжатия сам.
 */

import {
  CompressImageError,
  DecodedImage,
  decodeImage,
  hasAlphaChannel,
  renameForType,
  toBlob,
} from 'shared/utils/image/image-canvas';

/** Область обрезки в пикселях исходника — ровно то, что отдаёт `react-easy-crop`. */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** То же качество, что на первой ступени лестницы сжатия: визуально без потерь. */
export const CROP_QUALITY = 0.92;

/**
 * Обрезка по границам исходника.
 *
 * Кропер умеет отдавать область, выходящую за картинку (зум меньше единицы,
 * округление сторон), а `drawImage` с отрицательными координатами или шириной за
 * краем даёт прозрачные поля вместо пикселей.
 */
function clampArea(area: CropArea, image: DecodedImage): CropArea {
  const x = Math.max(0, Math.min(Math.round(area.x), image.width));
  const y = Math.max(0, Math.min(Math.round(area.y), image.height));
  const width = Math.max(0, Math.min(Math.round(area.width), image.width - x));
  const height = Math.max(0, Math.min(Math.round(area.height), image.height - y));

  return { x, y, width, height };
}

function drawCrop(image: DecodedImage, area: CropArea): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = area.width;
  canvas.height = area.height;

  const context = canvas.getContext('2d');
  if (!context) throw new CompressImageError('encode-failed', 'Канва недоступна');

  context.drawImage(image.source, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return canvas;
}

/**
 * Вырезает область из файла и отдаёт новый `File`.
 *
 * Результат всегда новый объект: даже область «во всю картинку» перекодируется,
 * потому что вызвать `cropImage` — уже решение пользователя обрезать. Путь «оставить
 * как есть» в дропзоне сюда не заходит и отдаёт исходный файл нетронутым.
 */
export async function cropImage(file: File, area: CropArea): Promise<File> {
  const image = await decodeImage(file);

  try {
    const cropped = clampArea(area, image);
    if (cropped.width < 1 || cropped.height < 1) {
      throw new CompressImageError('encode-failed', 'Область кадрирования пуста');
    }

    const type = hasAlphaChannel(image, file.type) ? 'image/webp' : 'image/jpeg';
    const canvas = drawCrop(image, cropped);
    const blob = await toBlob(canvas, type, CROP_QUALITY);

    // `canvas.toBlob` по спецификации молча отдаёт PNG, если формат не поддержан,
    // — расширение берём из реального типа блоба, а не из запрошенного.
    const outputType = blob.type || type;

    return new File([blob], renameForType(file.name, outputType), {
      type: outputType,
      lastModified: Date.now(),
    });
  } finally {
    image.close();
  }
}
